import { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useI18n, SUPPORTED_LANGUAGES } from '../i18n';
import { CountryFlag } from '../components/LanguageSwitcher';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { 
  setup2FA, verify2FA, disable2FA, 
  getMyLimits, requestLimitIncrease, submitVerification, 
  upgradeTier, getLimitRequests,
  getReceiptEmailSetting, updateReceiptEmailSetting,
  getWhatsAppPreferences, updateWhatsAppPreferences
} from '../lib/api';
import { toast } from 'sonner';
import { 
  User, 
  Shield, 
  Palette, 
  Loader2, 
  Check,
  AlertTriangle,
  Moon,
  Sun,
  Bell,
  BellOff,
  Globe,
  ChevronRight,
  TrendingUp,
  ArrowUpCircle,
  Wallet,
  Send,
  Download,
  CreditCard,
  FileText,
  Upload,
  Star,
  Crown,
  Gem,
  Info,
  Clock,
  CheckCircle2,
  XCircle,
  Search,
  Languages,
  AtSign,
  Mail,
  MessageCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import UserTagSection from '../components/UserTagSection';
import TagHistorySection from '../components/TagHistorySection';
import BiometricSettings from '../components/BiometricSettings';
import TransactionExport from '../components/TransactionExport';

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, changeLanguage, supportedLanguages, getCurrentLanguage, isRTL } = useI18n();
  const { 
    isSupported: pushSupported, 
    isSubscribed: pushSubscribed, 
    permission: pushPermission,
    loading: pushLoading, 
    subscribe: subscribePush, 
    unsubscribe: unsubscribePush 
  } = usePushNotifications();
  const [loading, setLoading] = useState(false);
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [languageSearch, setLanguageSearch] = useState('');
  
  // Limits state
  const [limitsData, setLimitsData] = useState(null);
  const [limitsLoading, setLimitsLoading] = useState(false);
  const [limitRequests, setLimitRequests] = useState([]);
  const [showIncreaseDialog, setShowIncreaseDialog] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [receiptEmailsEnabled, setReceiptEmailsEnabled] = useState(true);
  const [receiptEmailsLoading, setReceiptEmailsLoading] = useState(false);
  
  // WhatsApp preferences state
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappLanguage, setWhatsappLanguage] = useState('en');
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappError, setWhatsappError] = useState('');
  const [whatsappEditMode, setWhatsappEditMode] = useState(false);
  
  // WhatsApp language options
  const whatsappLanguageOptions = [
    { code: 'en', label: 'English' },
    { code: 'pt_BR', label: 'Português' },
    { code: 'fr', label: 'Français' }
  ];
  
  const [increaseForm, setIncreaseForm] = useState({
    limit_type: 'deposit_daily',
    requested_amount: '',
    reason: ''
  });
  const [verifyForm, setVerifyForm] = useState({
    document_type: 'id_card',
    document_url: '',
    document_number: ''
  });

  // Filter languages based on search
  const filteredLanguages = supportedLanguages.filter(lang => 
    lang.name.toLowerCase().includes(languageSearch.toLowerCase()) ||
    lang.nativeName.toLowerCase().includes(languageSearch.toLowerCase()) ||
    lang.code.toLowerCase().includes(languageSearch.toLowerCase())
  );

  const loadLimitsData = useCallback(async () => {
    setLimitsLoading(true);
    try {
      const [limitsRes, requestsRes] = await Promise.all([
        getMyLimits(),
        getLimitRequests()
      ]);
      setLimitsData(limitsRes.data);
      setLimitRequests(requestsRes.data.requests || []);
    } catch (error) {
      logger.error('Error loading limits:', error);
    } finally {
      setLimitsLoading(false);
    }
  }, []);

  // Load limits data
  useEffect(() => {
    loadLimitsData();
  }, [loadLimitsData]);

  // Load receipt email setting
  useEffect(() => {
    const loadReceiptSetting = async () => {
      try {
        const res = await getReceiptEmailSetting();
        setReceiptEmailsEnabled(res.data.receipt_emails_enabled);
      } catch (error) {
        logger.error('Error loading receipt email setting:', error);
      }
    };
    loadReceiptSetting();
  }, []);

  // Load WhatsApp preferences
  useEffect(() => {
    const loadWhatsAppPreferences = async () => {
      try {
        const res = await getWhatsAppPreferences();
        setWhatsappOptIn(res.data.whatsapp_opt_in || false);
        setWhatsappNumber(res.data.whatsapp_number || '');
        setWhatsappLanguage(res.data.whatsapp_language || 'en');
      } catch (error) {
        logger.error('Error loading WhatsApp preferences:', error);
      }
    };
    loadWhatsAppPreferences();
  }, []);

  // Validate WhatsApp number
  const validateWhatsAppNumber = (num) => {
    if (!num) return '';
    const cleaned = num.replace(/[\s\-\(\)]/g, '');
    const regex = /^\+[1-9]\d{7,14}$/;
    if (!regex.test(cleaned)) {
      return 'Invalid format. Use +[country code][number] (e.g., +31612345678)';
    }
    return '';
  };

  const handleWhatsAppNumberChange = (e) => {
    const value = e.target.value;
    setWhatsappNumber(value);
    if (value) {
      setWhatsappError(validateWhatsAppNumber(value));
    } else {
      setWhatsappError('');
    }
  };

  const handleSaveWhatsAppPreferences = async () => {
    if (whatsappOptIn && !whatsappNumber) {
      toast.error('Please enter your WhatsApp number');
      return;
    }
    
    if (whatsappOptIn && whatsappNumber) {
      const error = validateWhatsAppNumber(whatsappNumber);
      if (error) {
        setWhatsappError(error);
        toast.error(error);
        return;
      }
    }
    
    setWhatsappLoading(true);
    try {
      await updateWhatsAppPreferences({
        whatsapp_opt_in: whatsappOptIn,
        whatsapp_number: whatsappOptIn ? whatsappNumber.replace(/[\s\-\(\)]/g, '') : null,
        whatsapp_language: whatsappLanguage
      });
      toast.success(whatsappOptIn ? 'WhatsApp notifications enabled' : 'WhatsApp notifications disabled');
      setWhatsappEditMode(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to update WhatsApp preferences');
    } finally {
      setWhatsappLoading(false);
    }
  };

  const handleToggleReceiptEmails = async () => {
    setReceiptEmailsLoading(true);
    try {
      const newValue = !receiptEmailsEnabled;
      await updateReceiptEmailSetting(newValue);
      setReceiptEmailsEnabled(newValue);
      toast.success(newValue ? 'Receipt emails enabled' : 'Receipt emails disabled');
    } catch (error) {
      toast.error('Failed to update receipt email setting');
    } finally {
      setReceiptEmailsLoading(false);
    }
  };

  const handleTogglePush = async () => {
    if (pushSubscribed) {
      const success = await unsubscribePush();
      if (success) {
        toast.success('Push notifications disabled');
      } else {
        toast.error('Failed to disable notifications');
      }
    } else {
      const success = await subscribePush();
      if (success) {
        toast.success('Push notifications enabled');
      } else if (pushPermission === 'denied') {
        toast.error('Notification permission denied. Please enable in browser settings.');
      } else {
        toast.error('Failed to enable notifications');
      }
    }
  };

  const handleSetup2FA = async () => {
    setLoading(true);
    try {
      const response = await setup2FA();
      setTwoFASetup(response.data);
      toast.info('Scan the QR code with your authenticator app');
    } catch (error) {
      toast.error('Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      await verify2FA(verificationCode);
      toast.success('2FA enabled successfully!');
      setTwoFASetup(null);
      setVerificationCode('');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      await disable2FA(disableCode);
      toast.success('2FA disabled successfully');
      setDisableCode('');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestIncrease = async () => {
    if (!increaseForm.requested_amount || increaseForm.requested_amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (increaseForm.reason.length < 10) {
      toast.error('Please provide a detailed reason (min 10 characters)');
      return;
    }
    
    setLoading(true);
    try {
      await requestLimitIncrease({
        limit_type: increaseForm.limit_type,
        requested_amount: parseFloat(increaseForm.requested_amount),
        reason: increaseForm.reason
      });
      toast.success('Limit increase request submitted!');
      setShowIncreaseDialog(false);
      setIncreaseForm({ limit_type: 'deposit_daily', requested_amount: '', reason: '' });
      loadLimitsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitVerification = async () => {
    if (!verifyForm.document_url) {
      toast.error('Please provide a document URL');
      return;
    }
    
    setLoading(true);
    try {
      await submitVerification(verifyForm);
      toast.success('Verification submitted. We will review it shortly.');
      setShowVerifyDialog(false);
      setVerifyForm({ document_type: 'id_card', document_url: '', document_number: '' });
      loadLimitsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit verification');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeTier = async () => {
    setLoading(true);
    try {
      const res = await upgradeTier();
      toast.success(res.data.message);
      loadLimitsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upgrade tier');
    } finally {
      setLoading(false);
    }
  };

  const formatLimit = (value) => {
    if (value === null || value === undefined) return 'Unlimited';
    return `$${value.toLocaleString()}`;
  };

  const getUsagePercent = (used, limit) => {
    if (!limit) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'premium': return <Crown className="w-5 h-5 text-amber-500" />;
      case 'verified': return <Star className="w-5 h-5 text-blue-500" />;
      default: return <Gem className="w-5 h-5 text-emerald-500" />;
    }
  };

  const limitTypes = [
    { key: 'deposit_daily', label: 'Daily Deposit', icon: Download },
    { key: 'deposit_monthly', label: 'Monthly Deposit', icon: Download },
    { key: 'send_daily', label: 'Daily Send', icon: Send },
    { key: 'send_monthly', label: 'Monthly Send', icon: Send },
    { key: 'receive_daily', label: 'Daily Receive', icon: Wallet },
    { key: 'receive_monthly', label: 'Monthly Receive', icon: Wallet },
    { key: 'wallet_balance', label: 'Wallet Balance', icon: CreditCard },
    { key: 'single_transaction', label: 'Single Transaction', icon: ArrowUpCircle }
  ];

  return (
    <Layout>
      <motion.div 
        className="max-w-3xl mx-auto space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="settings-container"
      >
        <div>
          <h1 className="text-3xl font-heading font-bold">Settings</h1>
          <p className="text-muted-foreground">Manage your account preferences</p>
        </div>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-4 h-12 rounded-xl">
            <TabsTrigger value="profile" className="rounded-lg" data-testid="profile-tab">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="limits" className="rounded-lg" data-testid="limits-tab">
              <TrendingUp className="w-4 h-4 mr-2" />
              Limits
            </TabsTrigger>
            <TabsTrigger value="security" className="rounded-lg" data-testid="security-tab">
              <Shield className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
            <TabsTrigger value="preferences" className="rounded-lg" data-testid="preferences-tab">
              <Palette className="w-4 h-4 mr-2" />
              Preferences
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6 mt-6">
            {/* ParachPay Tag Section */}
            <UserTagSection user={user} />
            
            {/* Tag History Section */}
            <TagHistorySection />
            
            {/* Transaction Export */}
            <TransactionExport />
            
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Profile Information</CardTitle>
                <CardDescription>Your account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={user?.full_name || ''}
                    disabled
                    className="h-12 rounded-xl bg-muted"
                    data-testid="profile-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="h-12 rounded-xl bg-muted"
                    data-testid="profile-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Member Since</Label>
                  <Input
                    value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : ''}
                    disabled
                    className="h-12 rounded-xl bg-muted"
                    data-testid="profile-created"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Limits Tab */}
          <TabsContent value="limits" className="space-y-6 mt-6" data-testid="limits-content">
            {limitsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : limitsData ? (
              <>
                {/* Current Tier Card */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getTierIcon(limitsData.tier)}
                        <div>
                          <CardTitle className="font-heading">{limitsData.tier_name} Tier</CardTitle>
                          <CardDescription>{limitsData.tier_description}</CardDescription>
                        </div>
                      </div>
                      {limitsData.next_tier && (
                        <Badge variant="outline" className="text-sm">
                          Next: {limitsData.next_tier.charAt(0).toUpperCase() + limitsData.next_tier.slice(1)}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Verification Status */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(limitsData.verification_status).map(([key, value]) => (
                        <div 
                          key={key}
                          className={`p-3 rounded-xl flex items-center gap-2 ${
                            value ? 'bg-emerald-500/10' : 'bg-muted'
                          }`}
                        >
                          {value ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-muted-foreground" />
                          )}
                          <span className="text-sm capitalize">
                            {key.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Upgrade Requirements */}
                    {limitsData.upgrade_requirements.length > 0 && (
                      <div className="p-4 rounded-xl bg-orange-500/10 space-y-2">
                        <p className="font-medium text-orange-700 dark:text-orange-400">
                          Requirements for next tier:
                        </p>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {limitsData.upgrade_requirements.map((req) => (
                            <li key={req} className="flex items-center gap-2">
                              <Info className="w-4 h-4" />
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-3">
                      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="rounded-xl" data-testid="verify-identity-btn">
                            <Upload className="w-4 h-4 mr-2" />
                            Submit Verification
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle className="font-heading">Submit Verification Document</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label>Document Type</Label>
                              <select
                                value={verifyForm.document_type}
                                onChange={(e) => setVerifyForm({ ...verifyForm, document_type: e.target.value })}
                                className="w-full h-10 px-3 rounded-md border border-input bg-background"
                                data-testid="document-type-select"
                              >
                                <option value="id_card">ID Card</option>
                                <option value="passport">Passport</option>
                                <option value="drivers_license">Driver&apos;s License</option>
                                <option value="utility_bill">Utility Bill (Address Proof)</option>
                                <option value="bank_statement">Bank Statement (Address Proof)</option>
                              </select>
                            </div>
                            <div className="space-y-2">
                              <Label>Document URL</Label>
                              <Input
                                placeholder="https://example.com/document.jpg"
                                value={verifyForm.document_url}
                                onChange={(e) => setVerifyForm({ ...verifyForm, document_url: e.target.value })}
                                data-testid="document-url-input"
                              />
                              <p className="text-xs text-muted-foreground">
                                Upload your document to a secure service and paste the URL
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Label>Document Number (Optional)</Label>
                              <Input
                                placeholder="Document ID/Number"
                                value={verifyForm.document_number}
                                onChange={(e) => setVerifyForm({ ...verifyForm, document_number: e.target.value })}
                                data-testid="document-number-input"
                              />
                            </div>
                            <Button 
                              onClick={handleSubmitVerification} 
                              disabled={loading}
                              className="w-full rounded-xl"
                              data-testid="submit-verification-btn"
                            >
                              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Verification'}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>

                      {limitsData.can_upgrade && (
                        <Button 
                          onClick={handleUpgradeTier} 
                          disabled={loading}
                          className="rounded-xl"
                          data-testid="upgrade-tier-btn"
                        >
                          {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <ArrowUpCircle className="w-4 h-4 mr-2" />
                              Upgrade to {limitsData.next_tier?.charAt(0).toUpperCase() + limitsData.next_tier?.slice(1)}
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Current Limits & Usage */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-heading">Your Limits & Usage</CardTitle>
                        <CardDescription>Monitor your spending limits and current usage</CardDescription>
                      </div>
                      <Dialog open={showIncreaseDialog} onOpenChange={setShowIncreaseDialog}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="rounded-xl" data-testid="request-increase-btn">
                            <TrendingUp className="w-4 h-4 mr-2" />
                            Request Increase
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle className="font-heading">Request Limit Increase</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label>Limit Type</Label>
                              <select
                                value={increaseForm.limit_type}
                                onChange={(e) => setIncreaseForm({ ...increaseForm, limit_type: e.target.value })}
                                className="w-full h-10 px-3 rounded-md border border-input bg-background"
                                data-testid="limit-type-select"
                              >
                                {limitTypes.map(lt => (
                                  <option key={lt.key} value={lt.key}>{lt.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="space-y-2">
                              <Label>Current Limit</Label>
                              <Input
                                value={formatLimit(limitsData.limits[increaseForm.limit_type])}
                                disabled
                                className="bg-muted"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Requested New Limit ($)</Label>
                              <Input
                                type="number"
                                min="0"
                                value={increaseForm.requested_amount}
                                onChange={(e) => setIncreaseForm({ ...increaseForm, requested_amount: e.target.value })}
                                placeholder="Enter requested amount"
                                data-testid="requested-amount-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Reason for Increase</Label>
                              <Textarea
                                value={increaseForm.reason}
                                onChange={(e) => setIncreaseForm({ ...increaseForm, reason: e.target.value })}
                                placeholder="Please explain why you need a higher limit..."
                                rows={3}
                                data-testid="increase-reason-textarea"
                              />
                            </div>
                            <Button 
                              onClick={handleRequestIncrease} 
                              disabled={loading}
                              className="w-full rounded-xl"
                              data-testid="submit-increase-btn"
                            >
                              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Request'}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4">
                      {/* Deposit Limits */}
                      <div className="space-y-3">
                        <h4 className="font-medium flex items-center gap-2">
                          <Download className="w-4 h-4 text-emerald-500" />
                          Deposit Limits
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="p-4 rounded-xl bg-muted space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>Daily</span>
                              <span className="font-medium">${limitsData.usage.deposit_today?.toLocaleString() || 0} / {formatLimit(limitsData.limits.deposit_daily)}</span>
                            </div>
                            <Progress value={getUsagePercent(limitsData.usage.deposit_today, limitsData.limits.deposit_daily)} className="h-2" />
                          </div>
                          <div className="p-4 rounded-xl bg-muted space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>Monthly</span>
                              <span className="font-medium">${limitsData.usage.deposit_month?.toLocaleString() || 0} / {formatLimit(limitsData.limits.deposit_monthly)}</span>
                            </div>
                            <Progress value={getUsagePercent(limitsData.usage.deposit_month, limitsData.limits.deposit_monthly)} className="h-2" />
                          </div>
                        </div>
                      </div>

                      {/* Send Limits */}
                      <div className="space-y-3">
                        <h4 className="font-medium flex items-center gap-2">
                          <Send className="w-4 h-4 text-blue-500" />
                          Send Limits
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="p-4 rounded-xl bg-muted space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>Daily</span>
                              <span className="font-medium">${limitsData.usage.send_today?.toLocaleString() || 0} / {formatLimit(limitsData.limits.send_daily)}</span>
                            </div>
                            <Progress value={getUsagePercent(limitsData.usage.send_today, limitsData.limits.send_daily)} className="h-2" />
                          </div>
                          <div className="p-4 rounded-xl bg-muted space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>Monthly</span>
                              <span className="font-medium">${limitsData.usage.send_month?.toLocaleString() || 0} / {formatLimit(limitsData.limits.send_monthly)}</span>
                            </div>
                            <Progress value={getUsagePercent(limitsData.usage.send_month, limitsData.limits.send_monthly)} className="h-2" />
                          </div>
                        </div>
                      </div>

                      {/* Receive Limits */}
                      <div className="space-y-3">
                        <h4 className="font-medium flex items-center gap-2">
                          <Wallet className="w-4 h-4 text-purple-500" />
                          Receive Limits
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="p-4 rounded-xl bg-muted space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>Daily</span>
                              <span className="font-medium">${limitsData.usage.receive_today?.toLocaleString() || 0} / {formatLimit(limitsData.limits.receive_daily)}</span>
                            </div>
                            <Progress value={getUsagePercent(limitsData.usage.receive_today, limitsData.limits.receive_daily)} className="h-2" />
                          </div>
                          <div className="p-4 rounded-xl bg-muted space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>Monthly</span>
                              <span className="font-medium">${limitsData.usage.receive_month?.toLocaleString() || 0} / {formatLimit(limitsData.limits.receive_monthly)}</span>
                            </div>
                            <Progress value={getUsagePercent(limitsData.usage.receive_month, limitsData.limits.receive_monthly)} className="h-2" />
                          </div>
                        </div>
                      </div>

                      {/* Wallet & Transaction Limits */}
                      <div className="space-y-3">
                        <h4 className="font-medium flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-orange-500" />
                          Wallet & Transaction
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="p-4 rounded-xl bg-muted space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>Wallet Balance</span>
                              <span className="font-medium">${limitsData.usage.wallet_balance?.toLocaleString() || 0} / {formatLimit(limitsData.limits.wallet_balance)}</span>
                            </div>
                            <Progress value={getUsagePercent(limitsData.usage.wallet_balance, limitsData.limits.wallet_balance)} className="h-2" />
                          </div>
                          <div className="p-4 rounded-xl bg-muted">
                            <div className="flex justify-between text-sm">
                              <span>Single Transaction</span>
                              <span className="font-medium">{formatLimit(limitsData.limits.single_transaction)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Request History */}
                {limitRequests.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-heading">Limit Increase Requests</CardTitle>
                      <CardDescription>Your recent limit increase requests</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {limitRequests.slice(0, 5).map((req) => (
                          <div key={req.id} className="flex items-center justify-between p-4 rounded-xl bg-muted">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${
                                req.status === 'approved' ? 'bg-emerald-500/10' :
                                req.status === 'pending' ? 'bg-orange-500/10' : 'bg-red-500/10'
                              }`}>
                                {req.status === 'approved' ? (
                                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                ) : req.status === 'pending' ? (
                                  <Clock className="w-4 h-4 text-orange-500" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-500" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-sm capitalize">
                                  {req.limit_type.replace('_', ' ')}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Requested: ${req.requested_amount.toLocaleString()}
                                </p>
                              </div>
                            </div>
                            <Badge variant={
                              req.status === 'approved' ? 'default' :
                              req.status === 'pending' ? 'secondary' : 'destructive'
                            } className="capitalize">
                              {req.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <TrendingUp className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Unable to load limits data</p>
                  <Button onClick={loadLimitsData} variant="outline" className="mt-4 rounded-xl">
                    Retry
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6 mt-6">
            {/* Biometric Authentication (Native apps only) */}
            <BiometricSettings userEmail={user?.email} />
            
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-500" />
                  Two-Factor Authentication
                </CardTitle>
                <CardDescription>
                  Add an extra layer of security to your account
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {user?.two_fa_enabled ? (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10">
                      <Check className="w-5 h-5 text-emerald-500" />
                      <div>
                        <p className="font-medium text-emerald-700 dark:text-emerald-400">2FA Enabled</p>
                        <p className="text-sm text-muted-foreground">Your account is protected with two-factor authentication</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                        To disable 2FA, enter your current authenticator code
                      </div>
                      <div className="flex justify-center">
                        <InputOTP
                          maxLength={6}
                          value={disableCode}
                          onChange={setDisableCode}
                          data-testid="disable-2fa-otp"
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                      <Button
                        variant="destructive"
                        onClick={handleDisable2FA}
                        disabled={loading || disableCode.length !== 6}
                        className="w-full rounded-xl"
                        data-testid="disable-2fa-btn"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disable 2FA'}
                      </Button>
                    </div>
                  </div>
                ) : twoFASetup ? (
                  <div className="space-y-6">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-4">
                        Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                      </p>
                      <div className="inline-block p-4 bg-white rounded-xl">
                        <img 
                          src={twoFASetup.qr_code} 
                          alt="2FA QR Code" 
                          className="w-48 h-48"
                          data-testid="2fa-qr-code"
                        />
                      </div>
                    </div>

                    <div className="p-4 bg-muted rounded-xl">
                      <p className="text-xs text-muted-foreground mb-2">Manual entry key:</p>
                      <p className="font-mono text-sm break-all" data-testid="2fa-secret">{twoFASetup.secret}</p>
                    </div>

                    <div className="space-y-4">
                      <Label>Enter verification code from your app</Label>
                      <div className="flex justify-center">
                        <InputOTP
                          maxLength={6}
                          value={verificationCode}
                          onChange={setVerificationCode}
                          data-testid="verify-2fa-otp"
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setTwoFASetup(null);
                            setVerificationCode('');
                          }}
                          className="flex-1 rounded-xl"
                          data-testid="cancel-2fa-setup"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleVerify2FA}
                          disabled={loading || verificationCode.length !== 6}
                          className="flex-1 rounded-xl"
                          data-testid="verify-2fa-btn"
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Enable'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-muted">
                      <Shield className="w-5 h-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="font-medium">Protect your account</p>
                        <p className="text-sm text-muted-foreground">
                          Two-factor authentication adds an extra layer of security by requiring a code from your authenticator app when signing in.
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleSetup2FA}
                      disabled={loading}
                      className="w-full rounded-xl"
                      data-testid="setup-2fa-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Shield className="w-4 h-4 mr-2" />
                          Enable 2FA
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Appearance</CardTitle>
                <CardDescription>Customize how ParachPay looks</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 rounded-xl bg-muted">
                  <div className="flex items-center gap-3">
                    {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                    <div>
                      <p className="font-medium">Dark Mode</p>
                      <p className="text-sm text-muted-foreground">
                        {theme === 'dark' ? 'Currently using dark theme' : 'Currently using light theme'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={theme === 'dark'}
                    onCheckedChange={toggleTheme}
                    data-testid="theme-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Language Settings - Full 27 Languages */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Languages className="w-5 h-5 text-blue-500" />
                      Language Settings
                    </CardTitle>
                    <CardDescription>Choose your preferred language from 27 available options</CardDescription>
                  </div>
                  {isRTL && (
                    <Badge variant="outline" className="text-orange-500 border-orange-500">
                      RTL Mode Active
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Language Display */}
                <div className="p-4 rounded-xl bg-emerald-500/10 border-2 border-emerald-500">
                  <div className="flex items-center gap-3">
                    <CountryFlag countryCode={getCurrentLanguage()?.flag || 'US'} size="xl" />
                    <div className="flex-1">
                      <p className="font-semibold text-emerald-700 dark:text-emerald-400">
                        Current: {getCurrentLanguage()?.nativeName}
                      </p>
                      <p className="text-sm text-muted-foreground">{getCurrentLanguage()?.name}</p>
                    </div>
                    <Check className="w-6 h-6 text-emerald-500" />
                  </div>
                </div>

                {/* Search Languages */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search languages..."
                    value={languageSearch}
                    onChange={(e) => setLanguageSearch(e.target.value)}
                    className="pl-10 h-12 rounded-xl"
                    data-testid="language-search-input"
                  />
                </div>

                {/* Language Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-2">
                  {filteredLanguages.map((lang) => {
                    const isSelected = language === lang.code;
                    return (
                      <button
                        key={lang.code}
                        onClick={() => {
                          changeLanguage(lang.code);
                          toast.success(`Language changed to ${lang.name}`);
                        }}
                        className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-200 text-left ${
                          isSelected 
                            ? 'bg-emerald-500/10 border-2 border-emerald-500 shadow-sm' 
                            : 'bg-muted hover:bg-muted/80 border-2 border-transparent hover:border-border'
                        }`}
                        data-testid={`lang-${lang.code}`}
                      >
                        <CountryFlag countryCode={lang.flag} size="lg" />
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-sm truncate ${isSelected ? 'text-emerald-700 dark:text-emerald-400' : ''}`}>
                            {lang.nativeName}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">{lang.name}</p>
                        </div>
                        {isSelected && (
                          <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        )}
                        {lang.dir === 'rtl' && !isSelected && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 flex-shrink-0">
                            RTL
                          </Badge>
                        )}
                      </button>
                    );
                  })}
                </div>

                {filteredLanguages.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Globe className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No languages found matching &ldquo;{languageSearch}&rdquo;</p>
                  </div>
                )}

                {/* Language Count */}
                <p className="text-xs text-muted-foreground text-center pt-2">
                  {filteredLanguages.length} of {supportedLanguages.length} languages available
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Notifications</CardTitle>
                <CardDescription>Manage how you receive alerts</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-muted">
                  <div className="flex items-center gap-3">
                    {pushSubscribed ? <Bell className="w-5 h-5 text-emerald-500" /> : <BellOff className="w-5 h-5 text-muted-foreground" />}
                    <div>
                      <p className="font-medium">Push Notifications</p>
                      <p className="text-sm text-muted-foreground">
                        {!pushSupported 
                          ? 'Not supported in this browser' 
                          : pushSubscribed 
                            ? 'Receive alerts for spending limits and transfers' 
                            : 'Enable to get real-time alerts'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={pushSubscribed}
                    onCheckedChange={handleTogglePush}
                    disabled={!pushSupported || pushLoading}
                    data-testid="push-notification-toggle"
                  />
                </div>
                {pushPermission === 'denied' && (
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-destructive/10 text-destructive text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    Notifications blocked. Enable in your browser settings.
                  </div>
                )}

                {/* Receipt Email Setting */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-muted">
                  <div className="flex items-center gap-3">
                    <Mail className={`w-5 h-5 ${receiptEmailsEnabled ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                    <div>
                      <p className="font-medium">Receipt Emails</p>
                      <p className="text-sm text-muted-foreground">
                        {receiptEmailsEnabled 
                          ? 'Receive PDF receipts for all transactions' 
                          : 'Enable to get receipts via email'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={receiptEmailsEnabled}
                    onCheckedChange={handleToggleReceiptEmails}
                    disabled={receiptEmailsLoading}
                    data-testid="receipt-email-toggle"
                  />
                </div>

                {/* WhatsApp Notifications Setting */}
                <div className="p-4 rounded-xl bg-muted space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <MessageCircle className={`w-5 h-5 ${whatsappOptIn ? 'text-green-500' : 'text-muted-foreground'}`} />
                      <div>
                        <p className="font-medium">WhatsApp Notifications</p>
                        <p className="text-sm text-muted-foreground">
                          {whatsappOptIn 
                            ? 'Receive payment updates via WhatsApp' 
                            : 'Enable to get alerts on WhatsApp'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={whatsappOptIn}
                      onCheckedChange={(checked) => {
                        setWhatsappOptIn(checked);
                        if (checked || whatsappNumber) {
                          setWhatsappEditMode(true);
                        } else {
                          // If turning off and no number, save immediately
                          handleSaveWhatsAppPreferences();
                        }
                      }}
                      disabled={whatsappLoading}
                      data-testid="whatsapp-notification-toggle"
                    />
                  </div>
                  
                  {(whatsappOptIn || whatsappEditMode) && (
                    <div className="space-y-3 pt-2 border-t border-border">
                      <div className="space-y-2">
                        <Label htmlFor="whatsappNumber" className="text-sm">WhatsApp Number</Label>
                        <Input
                          id="whatsappNumber"
                          type="tel"
                          placeholder="+31612345678"
                          value={whatsappNumber}
                          onChange={handleWhatsAppNumberChange}
                          className={`h-10 rounded-lg ${whatsappError ? 'border-destructive' : ''}`}
                          data-testid="whatsapp-number-settings"
                        />
                        {whatsappError && (
                          <p className="text-xs text-destructive">{whatsappError}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Enter with country code (e.g., +31 for Netherlands). Message rates may apply.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="whatsappLanguage" className="text-sm">Message Language</Label>
                        <select
                          id="whatsappLanguage"
                          value={whatsappLanguage}
                          onChange={(e) => setWhatsappLanguage(e.target.value)}
                          className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                          data-testid="whatsapp-language-select"
                        >
                          {whatsappLanguageOptions.map((opt) => (
                            <option key={opt.code} value={opt.code}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      
                      <Button
                        onClick={handleSaveWhatsAppPreferences}
                        disabled={whatsappLoading}
                        size="sm"
                        className="rounded-lg"
                        data-testid="save-whatsapp-btn"
                      >
                        {whatsappLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Save WhatsApp Preferences
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Fee Information</CardTitle>
                <CardDescription>Current transaction fee structure</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 rounded-lg bg-muted">
                    <span className="text-sm">Deposit Fee</span>
                    <span className="font-mono font-medium text-orange-500">0.5%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg bg-muted">
                    <span className="text-sm">Transfer Fee</span>
                    <span className="font-mono font-medium text-orange-500">1.5%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg bg-muted">
                    <span className="text-sm">Exchange Fee</span>
                    <span className="font-mono font-medium text-orange-500">2.5%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg bg-muted">
                    <span className="text-sm">Receiving Funds</span>
                    <span className="font-mono font-medium text-emerald-500">FREE</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </motion.div>
    </Layout>
  );
};

export default Settings;
